import { createApi } from '@reduxjs/toolkit/query/react'

import { ActividadMaestraBusinessItem, ActividadMaestraBusinessResponse, BookingData } from '../interfaces/BookingData'

import { baseQuery } from '@/core/api/baseQuery'
import { GenericResponseOData } from '@/core/api/interfaces/GenericResponseOData'
import { unwrapPreserve } from '@/core/api/unwrapPreserve'
import { getEstadosLogicos } from '@/modules/Home/constants/home'
import { EstadoLogicoReserva } from '@/modules/Home/interfaces/EstadoLogicoVerticalReserva'
import { ReservasCounter } from '@/modules/Home/interfaces/ReservasCounter'

// Función para transformar la respuesta del nuevo endpoint al formato esperado
const transformActividadMaestraBusinessResponse = (
  response: ActividadMaestraBusinessResponse,
): GenericResponseOData<BookingData[]> => {
  const transformedValue: BookingData[] = response.value.map((item: ActividadMaestraBusinessItem) => {
    const actividades = (item.actividad ?? []).map((act) => ({
      ActividadId: act.actividadId,
      AsignacionCount: act['AsignacionProveedorActividad@odata.count'],
      'AsignacionProveedorActividad@odata.count': act['AsignacionProveedorActividad@odata.count'],
      AsignacionTotal: act['AsignacionProveedorActividad@odata.count'],
      // Pendiente de gestión: el servicio NO tiene el check CFM FACT. Si la respuesta no trae el
      // campo —backend todavía sin desplegar— se asume no gestionado: el contador queda inflado,
      // pero la tarjeta sigue mostrando datos en vez de vaciarse por el filtro `> 0` del grid.
      ConfirmacionFactibilidad: act.confirmacionFactibilidad ?? false,
    }))

    // Las anotaciones `...@odata.count` las emite el backend con un [JsonPropertyName] explicito.
    // Si la respuesta no las trae, la lista que SI vino es la mejor respuesta disponible: es la
    // cantidad de actividades que el backend devolvio para este booking. Antes esto quedaba en
    // `undefined` y terminaba sumandose, produciendo el «Servicios Totales: NaN» de la cabecera.
    const totalActividades = item['Actividad@odata.count'] ?? actividades.length

    return {
      BookingId: item.bookingId,
      BookingCode: item.bookingCode,
      FechaInicio: item.fechaInicio,
      ActividadCount: totalActividades,
      'Actividad@odata.count': totalActividades,
      Actividad: actividades,
      Estado: {
        Nombre: item.estado?.nombre ?? '',
      },
      EjecutivoComercial: {
        EjecutivoComercialId: item.ejecutivoComercial?.ejecutivoComercialId ?? 0,
        NombreCompleto: item.ejecutivoComercial?.nombreCompleto ?? '',
      },
      SalesProfit: {
        Valor: item.salesProfit?.valor ?? '',
      },
      EjecutivoOperativo: {
        NombreCompleto: item.ejecutivoOperativo?.nombreCompleto ?? '',
      },
    }
  })

  return {
    // OJO: interpolar directo convertia un valor ausente en la CADENA "undefined", que despues
    // pasaba de largo por el `?? '0'` de la pantalla —una cadena no vacia no es nullish— y se
    // pintaba tal cual en «Oportunidades Totales: undefined». Sin la anotacion, la cantidad de
    // filas devueltas es el total real: este endpoint no pagina, el grid pagina del lado del
    // navegador.
    '@odata.count': `${response['@odata.count'] ?? transformedValue.length}`,
    value: transformedValue,
  }
}

// Las respuestas de los controllers de negocio pasan por ReferenceHandler.Preserve, así que hay
// que aplanarlas antes de leerlas. (Los endpoints OData, como EstadoFisicoReservas, no: los
// serializa el formatter de OData y ya llegan planos.)
const aplanarActividadMaestra = (response: unknown): GenericResponseOData<BookingData[]> =>
  transformActividadMaestraBusinessResponse(unwrapPreserve(response) as ActividadMaestraBusinessResponse)

export const homeCountersApi = createApi({
  reducerPath: 'homeCountersApi',
  baseQuery,
  endpoints: (builder) => ({
    getOperativeCounter: builder.query<GenericResponseOData<BookingData[]>, { inicio: string; fin: string }>({
      query: ({ inicio, fin }) => ({
        url: `/ActividadMaestraBusiness?fechaInicio=${encodeURIComponent(inicio)}&fechaFin=${encodeURIComponent(fin)}`,
        method: 'GET',
      }),
      transformResponse: aplanarActividadMaestra,
    }),
    getAllOperativeCounter: builder.query<GenericResponseOData<BookingData[]>, { inicio: string; fin: string }>({
      query: ({ inicio, fin }) => ({
        url: `/ActividadMaestraBusiness?fechaInicio=${encodeURIComponent(inicio)}&fechaFin=${encodeURIComponent(fin)}`,
        method: 'GET',
      }),
      transformResponse: aplanarActividadMaestra,
    }),
    getReservasCounter: builder.query<ReservasCounter, { inicio: string; filtroEstadoHorizontal?: string }>({
      query: ({ inicio, filtroEstadoHorizontal }) => ({
        url: `/ReservasCounterBusiness?fechaInicio=${encodeURIComponent(inicio)}${
          filtroEstadoHorizontal ? `&filtroEstadoHorizontal=${encodeURIComponent(filtroEstadoHorizontal)}` : ''
        }`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) => unwrapPreserve(response) as ReservasCounter,
    }),
    getEstadosLogicos: builder.query<GenericResponseOData<EstadoLogicoReserva[]>, void>({
      query: () => ({
        url: `/EstadoFisicoReservas?${getEstadosLogicos}`,
        method: 'GET',
      }),
    }),
  }),
})

export const {
  useLazyGetOperativeCounterQuery,
  useLazyGetAllOperativeCounterQuery,
  useLazyGetReservasCounterQuery,
  useLazyGetEstadosLogicosQuery,
} = homeCountersApi
